/**
 * PanelFunctions.js
 * 
 * This module provides utility functions for managing solar panels in the SightFlight Designer application.
 * It contains functions for selecting, deselecting, deleting, and validating panels, as well as
 * changing panel orientation and cleaning up visual elements.
 * 
 * These functions work in conjunction with the PanelManager class to provide a complete
 * panel management system for the application.
 */

import * as THREE from "three";
import { COLORS } from "../Global/materials";
import { POLYGONS } from "../Global/variables";
import { sceneManager } from "../SetUps/SceneManager";
import PanelManager from "./PanelManager";
import { panelInSegment } from "./Placement";
import { createSegmentPlane } from "../Global/functions";
import {
  createWireframe,
  getEdgeDirection,
  getPanelTiltLiftOffset,
  getPanelWireframe,
  resolvePanelTransform,
  setPanelWireframeStyle,
} from "./Helpers";
import { pushUndoAction } from "../Global/UndoManager";
import { getPanelImage } from "../../helpers/panelImageCache";

const PANEL_QUATERNION_EPS = 1e-8;
const INCH_TO_METER = 1 / 39.37;

const applyPanelEmissiveVisual = (panel, colorHex = COLORS.black, intensity = 0) => {
  const material = panel?.material;
  if (!material?.emissive?.set) return;
  material.emissive.set(colorHex);
  material.emissiveIntensity = intensity;
};

const applyPanelSelectionVisual = (panel, segmentIndex, isSelected) => {
  if (!panel || segmentIndex == null) return;
  panel.userData = {
    ...(panel.userData || {}),
    isSelected: isSelected === true,
  };

  if (isSelected) {
    setPanelWireframeStyle(panel, { opacity: 1, color: COLORS.yellow });
    applyPanelEmissiveVisual(panel, COLORS.yellow, 0.25);
    return;
  }

  const isValid = panelInSegment(panel, segmentIndex);
  if (!isValid) {
    setPanelWireframeStyle(panel, { opacity: 1, color: COLORS.red });
    applyPanelEmissiveVisual(panel, COLORS.red, 0.18);
    return;
  }
  setPanelWireframeStyle(panel, { opacity: 0, color: COLORS.red });
  applyPanelEmissiveVisual(panel, COLORS.black, 0);
};

export const syncPanelSelectionVisuals = (segmentIndex, panelIndices = []) => {
  if (segmentIndex == null) return;
  const segment = POLYGONS[segmentIndex];
  if (!segment || !Array.isArray(segment.panels)) return;

  const selectedSet = new Set(
    (Array.isArray(panelIndices) ? panelIndices : [])
      .filter((idx) => Number.isInteger(idx) && idx >= 0 && idx < segment.panels.length)
  );

  segment.panels.forEach((panelRecord, index) => {
    applyPanelSelectionVisual(panelRecord?.panel, segmentIndex, selectedSet.has(index));
  });
};

const getSegmentDirection = (segmentIndex) => {
  const segment = POLYGONS[segmentIndex];
  if (!segment) return null;

  const plane = createSegmentPlane(segmentIndex);
  const orientation = plane.normal.clone().normalize();
  if (orientation.z < 0) {
    orientation.negate();
    plane.negate();
  }

  const direction = getEdgeDirection(segment, orientation);
  return { orientation, direction, plane };
};

const applyTransformToPanel = (panelMesh, segmentIndex, panelData = {}) => {
  const segmentFrame = getSegmentDirection(segmentIndex);
  if (!segmentFrame || !panelMesh) return null;

  const transform = resolvePanelTransform(
    segmentFrame.orientation,
    segmentFrame.direction,
    panelData
  );

  const panelHeight = Number(panelMesh?.geometry?.parameters?.height || 0);
  const previousTilt =
    Number(panelMesh?.userData?.panelTransform?.tilt ?? panelData?.tilt ?? 0);
  const currentDistance = segmentFrame.plane.distanceToPoint(panelMesh.position);
  const currentLift = getPanelTiltLiftOffset(panelHeight, previousTilt);
  const explicitBaseOffsetM = Number(panelData?.offset) * INCH_TO_METER;
  const hasExplicitBaseOffset = Number.isFinite(explicitBaseOffsetM);
  const baseOffsetM = hasExplicitBaseOffset
    ? Math.max(0, explicitBaseOffsetM)
    : Math.max(0, currentDistance - currentLift);
  const nextLift = getPanelTiltLiftOffset(panelHeight, transform.tilt);
  const anchoredPoint = panelMesh.position
    .clone()
    .add(segmentFrame.orientation.clone().multiplyScalar(-currentDistance));
  const nextCenterDistance = baseOffsetM + nextLift;

  panelMesh.quaternion.copy(transform.quaternion);
  panelMesh.position.copy(
    anchoredPoint.add(
      segmentFrame.orientation.clone().multiplyScalar(nextCenterDistance)
    )
  );
  panelMesh.userData.panelTransform = {
    tilt: transform.tilt,
    rotation: transform.rotation,
    azimuth: transform.azimuth,
    segmentIndex,
  };

  return transform;
};

const hasQuaternionChanged = (from, to) => {
  if (!from || !to) return false;
  const dot = Math.abs(from.dot(to));
  return 1 - dot > PANEL_QUATERNION_EPS;
};

const hasPanelUiChanged = (beforeUi, afterUi) => {
  const trackedKeys = ["tilt", "rotation", "azimuth"];
  return trackedKeys.some((key) => Number(beforeUi?.[key]) !== Number(afterUi?.[key]));
};

const patchPanelsUi = (setPolygons, segmentIndex, snapshots, phase) => {
  if (typeof setPolygons !== "function") return;
  setPolygons((prev) => {
    if (!prev || !prev[segmentIndex]) return prev;

    const next = [...prev];
    const segment = { ...next[segmentIndex] };
    const panels = Array.isArray(segment.panels) ? [...segment.panels] : [];

    snapshots.forEach((snap) => {
      const patch = phase === "after" ? snap.afterUi : snap.beforeUi;
      if (!panels[snap.index] || !patch) return;
      panels[snap.index] = { ...panels[snap.index], ...patch };
    });

    segment.panels = panels;
    next[segmentIndex] = segment;
    return next;
  });
};

const getPanelCatalogById = (panelList = [], selectedId) => {
  const list = Array.isArray(panelList) ? panelList : [];
  return list.find((item) => String(item?.id) === String(selectedId)) || null;
};

const toPanelMeters = (panelData = {}) => {
  if (!panelData) return { widthM: 0, heightM: 0 };

  if (panelData.unit === "Metric") {
    const widthDivisor = panelData.width_unit === "mm" ? 1000 : 100;
    const heightDivisor = panelData.height_unit === "mm" ? 1000 : 100;
    return {
      widthM: Number(panelData.width || 0) / widthDivisor,
      heightM: Number(panelData.height || 0) / heightDivisor,
    };
  }

  if (panelData.unit === "Imperial") {
    return {
      widthM: Number(panelData.width || 0) / 39.37,
      heightM: Number(panelData.height || 0) / 39.37,
    };
  }

  return { widthM: 0, heightM: 0 };
};

const getOrientedDimensions = (panelData, orientation = "Portrait") => {
  let { widthM, heightM } = toPanelMeters(panelData);
  if (orientation === "Landscape") {
    [widthM, heightM] = [heightM, widthM];
  }
  return {
    widthM,
    heightM,
    areaSqft: widthM * heightM * 10.764,
  };
};

const toInchNumber = (value, fallback = 1) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(0, parsed);
};

const toMetersFromInches = (value, fallback = 1) =>
  toInchNumber(value, fallback) * INCH_TO_METER;

const getPanelLayoutSpacingMeters = (panelUi = {}, panelUpdates = {}) => {
  const colInches =
    panelUpdates?.moduleSpacingCol ??
    panelUi?.moduleSpacingCol ??
    1;
  const rowInches =
    panelUpdates?.moduleSpacingRow ??
    panelUi?.moduleSpacingRow ??
    1;

  return {
    colM: toMetersFromInches(colInches, 1),
    rowM: toMetersFromInches(rowInches, 1),
  };
};

const reflowUpdatedPanelLayout = (segmentIndex, snapshots = [], panelUpdates = {}) => {
  if (segmentIndex == null || !Array.isArray(snapshots) || snapshots.length <= 1) return;

  const frame = getSegmentDirection(segmentIndex);
  if (!frame) return;

  const { orientation, direction, plane } = frame;
  const planeOrigin = orientation.clone().multiplyScalar(-plane.constant);
  const uAxis = direction.clone().normalize();

  let vAxis = new THREE.Vector3().crossVectors(orientation, uAxis);
  if (vAxis.lengthSq() <= 1e-12) {
    const fallback = Math.abs(orientation.z) < 0.9
      ? new THREE.Vector3(0, 0, 1)
      : new THREE.Vector3(1, 0, 0);
    vAxis = new THREE.Vector3().crossVectors(orientation, fallback);
  }
  vAxis.normalize();

  const items = snapshots
    .map((snap) => {
      const panelMesh = snap?.panel;
      if (!panelMesh) return null;

      const panelHeight = Number(panelMesh?.geometry?.parameters?.height || 0);
      const panelWidth = Number(panelMesh?.geometry?.parameters?.width || 0);
      if (!(panelHeight > 0 && panelWidth > 0)) return null;

      const transform = panelMesh.userData?.panelTransform || {};
      const tilt = Number(transform.tilt ?? snap?.afterUi?.tilt ?? snap?.beforeUi?.tilt ?? 0);
      const currentDistance = plane.distanceToPoint(panelMesh.position);
      const lift = getPanelTiltLiftOffset(panelHeight, tilt);
      const baseOffsetM = Math.max(0, currentDistance - lift);

      const anchorPoint = panelMesh.position
        .clone()
        .add(orientation.clone().multiplyScalar(-currentDistance));
      const rel = anchorPoint.clone().sub(planeOrigin);

      const spacing = getPanelLayoutSpacingMeters(snap?.afterUi, panelUpdates);

      return {
        ...snap,
        panel: panelMesh,
        width: panelWidth,
        height: panelHeight,
        tilt,
        baseOffsetM,
        u: rel.dot(uAxis),
        v: rel.dot(vAxis),
        colSpacingM: spacing.colM,
        rowSpacingM: spacing.rowM,
      };
    })
    .filter(Boolean);

  if (items.length <= 1) return;

  const avgHeight =
    items.reduce((sum, item) => sum + item.height, 0) / Math.max(1, items.length);
  const rowTolerance = Math.max(avgHeight * 0.6, 0.08);
  const orderedByV = [...items].sort((a, b) => b.v - a.v);
  const rows = [];

  orderedByV.forEach((item) => {
    let bestRow = null;
    let bestDiff = Infinity;

    rows.forEach((row) => {
      const diff = Math.abs(item.v - row.meanV);
      if (diff < bestDiff && diff <= rowTolerance) {
        bestDiff = diff;
        bestRow = row;
      }
    });

    if (!bestRow) {
      rows.push({
        meanV: item.v,
        items: [item],
      });
      return;
    }

    bestRow.items.push(item);
    bestRow.meanV =
      bestRow.items.reduce((sum, rowItem) => sum + rowItem.v, 0) / bestRow.items.length;
  });

  rows.sort((a, b) => b.meanV - a.meanV);

  const defaultRowSpacing =
    items.reduce((sum, item) => sum + item.rowSpacingM, 0) / Math.max(1, items.length);
  let previousRowTop = null;
  let previousRowHeight = 0;

  rows.forEach((row) => {
    row.items.sort((a, b) => a.u - b.u);

    const rowStartU = row.items[0]?.u ?? 0;
    const rowSpacing =
      row.items.reduce((sum, item) => sum + item.rowSpacingM, 0) /
      Math.max(1, row.items.length);
    const rowMaxHeight = row.items.reduce((max, item) => Math.max(max, item.height), 0);

    const targetV =
      previousRowTop == null
        ? row.meanV
        : previousRowTop - previousRowHeight - (rowSpacing || defaultRowSpacing);

    let cursorU = rowStartU;

    row.items.forEach((item, itemIndex) => {
      const targetU = itemIndex === 0 ? rowStartU : cursorU;
      const targetLift = getPanelTiltLiftOffset(item.height, item.tilt);
      const targetCenterDistance = item.baseOffsetM + targetLift;
      const targetAnchor = planeOrigin
        .clone()
        .add(uAxis.clone().multiplyScalar(targetU))
        .add(vAxis.clone().multiplyScalar(targetV));

      item.panel.position.copy(
        targetAnchor.add(orientation.clone().multiplyScalar(targetCenterDistance))
      );
      item.afterPosition = item.panel.position.clone();

      cursorU = targetU + item.width + item.colSpacingM;
    });

    previousRowTop = targetV;
    previousRowHeight = rowMaxHeight;
  });
};

const refreshPanelWireframe = (panelMesh) => {
  if (!panelMesh) return;
  const wireframe = getPanelWireframe(panelMesh);
  const prevOpacity = wireframe?.material?.opacity ?? 0;
  const prevColor = wireframe?.material?.color?.getHex?.() ?? COLORS.red;

  if (wireframe) {
    panelMesh.remove(wireframe);
    wireframe.geometry?.dispose?.();
    wireframe.material?.dispose?.();
  }

  createWireframe(panelMesh);
  const nextWireframe = getPanelWireframe(panelMesh);
  if (nextWireframe?.material) {
    nextWireframe.material.setValues({ opacity: prevOpacity, color: prevColor });
  }
};

const applyPanelTexture = (panelMesh, moduleId, orientation = "Portrait") => {
  const material = panelMesh?.material;
  if (!material) return;

  const textureUrl = getPanelImage(moduleId);
  if (!textureUrl) return;

  const loader = new THREE.TextureLoader();
  loader.load(
    textureUrl,
    (texture) => {
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.repeat.set(1, 1);
      texture.rotation = orientation === "Landscape" ? Math.PI / 2 : 0;

      material.map = texture;
      material.needsUpdate = true;
    },
    undefined,
    (error) => {
      console.error("Panel texture update failed:", error);
    }
  );
};

/**
 * Selects all panels in the currently selected segment
 * 
 * This function highlights all panels in the current segment with a yellow outline
 * and updates the selection state to include all panel indices.
 * 
 * @param {Object} selectedIndices - Object containing current selection indices
 * @param {Function} setSelectedIndices - State setter function for selection indices
 * @param {Function} setShowPanelSettings - State setter function to control panel settings visibility
 */
export function selectAllPanels(selectedIndices, setSelectedIndices, setShowPanelSettings) {
  // Get the currently selected segment index
  const { segmentIndex } = selectedIndices;
  
  // Get all panels in the segment
  const panels = POLYGONS[segmentIndex].panels;
  
  // Array to store indices of all panels
  let indices = [];
  
  // Loop through all panels and highlight them
  for (let i = 0; i < panels.length; i++) {
    const panel = panels[i].panel;
    applyPanelSelectionVisual(panel, segmentIndex, true);
    
    // Show the panel settings UI
    setShowPanelSettings(true);
    
    // Add the panel index to the selected indices
    indices.push(i);
  }
  
  // Update the selection state with all panel indices
  setSelectedIndices({ segmentIndex: segmentIndex, obstacleIndex: null, panelIndex: indices });
}

/**
 * Selects a single panel in the currently selected segment
 * 
 * This function highlights the specified panel and updates the selection state.
 * It can either replace the current selection or add to it.
 * 
 * @param {number} panelIndexToAdd - Index of the panel to select
 * @param {Function} setSelectedIndices - State setter function for selection indices
 * @param {Object} selectedIndices - Object containing current selection indices
 * @param {Function} setShowPanelSettings - State setter function to control panel settings visibility
 * @param {Object} options - Selection behavior options
 * @param {boolean} options.append - Whether to append to existing selection (Shift+click)
 */
export function selectSinglePanel(
  panelIndexToAdd,
  setSelectedIndices,
  selectedIndices,
  setShowPanelSettings,
  options = {}
) {
  // Get current selection information
  const { segmentIndex, panelIndex } = selectedIndices;
  const { append = false } = options;
  // Get the segment and panel to select
  const segment = POLYGONS[segmentIndex] ?? null;
  if (!segment || !Array.isArray(segment.panels)) return;

  const panelRecord = segment.panels[panelIndexToAdd];
  const panel = panelRecord?.panel ?? null;

  // Only proceed if panel exists
  if (panel) {
    const existingSelection = Array.isArray(panelIndex) ? panelIndex : [];

    // Normal click on an already-selected panel should preserve current multi-selection
    // so the user can drag the selected group without holding Shift.
    if (!append && existingSelection.includes(panelIndexToAdd)) {
      applyPanelSelectionVisual(panel, segmentIndex, true);
      setShowPanelSettings(true);
      setSelectedIndices({
        segmentIndex,
        obstacleIndex: null,
        panelIndex: existingSelection,
      });
      return;
    }

    if (!append) {
      // Replace selection: remove highlight from previously selected panels except the target.
      for (const index of existingSelection) {
        if (index === panelIndexToAdd) continue;
        const prevPanel = segment.panels[index]?.panel;
        applyPanelSelectionVisual(prevPanel, segmentIndex, false);
      }
    }

    applyPanelSelectionVisual(panel, segmentIndex, true);
    
    // Show the panel settings UI
    setShowPanelSettings(true);

    if (append) {
      if (existingSelection.includes(panelIndexToAdd)) {
        // Already selected; keep current multi-selection.
        applyPanelSelectionVisual(panel, segmentIndex, true);
        setSelectedIndices({
          segmentIndex,
          obstacleIndex: null,
          panelIndex: existingSelection,
        });
        return;
      }

      setSelectedIndices({
        segmentIndex,
        obstacleIndex: null,
        panelIndex: [...existingSelection, panelIndexToAdd],
      });
      return;
    }

    // Normal click: select only this panel.
    setSelectedIndices({
      segmentIndex,
      obstacleIndex: null,
      panelIndex: [panelIndexToAdd],
    });
  }
}

/**
 * Replaces the current panel selection with an explicit index list.
 *
 * This is intended for marquee/box selection workflows where multiple panels
 * are selected at once and non-panel objects must be ignored.
 *
 * @param {Array<number>} indices - Panel indices to select
 * @param {Function} setSelectedIndices - State setter for selected indices
 * @param {Object} selectedIndices - Current selected indices state
 * @param {Function} setShowPanelSettings - State setter for panel settings visibility
 */
export function selectPanelsByIndices(
  indices,
  setSelectedIndices,
  selectedIndices,
  setShowPanelSettings
) {
  const segmentIndex = selectedIndices?.segmentIndex;
  if (segmentIndex == null) return;

  const segment = POLYGONS[segmentIndex] ?? null;
  if (!segment || !Array.isArray(segment.panels)) return;

  const nextSelection = [...new Set(Array.isArray(indices) ? indices : [])]
    .filter((idx) => Number.isInteger(idx) && idx >= 0 && idx < segment.panels.length)
    .sort((a, b) => a - b);

  const previousSelection = Array.isArray(selectedIndices?.panelIndex)
    ? selectedIndices.panelIndex
    : [];

  for (const idx of previousSelection) {
    if (nextSelection.includes(idx)) continue;
    const panel = segment.panels[idx]?.panel;
    applyPanelSelectionVisual(panel, segmentIndex, false);
  }

  for (const idx of nextSelection) {
    const panel = segment.panels[idx]?.panel;
    applyPanelSelectionVisual(panel, segmentIndex, true);
  }

  if (typeof setShowPanelSettings === "function") {
    setShowPanelSettings(true);
  }

  setSelectedIndices({
    segmentIndex,
    obstacleIndex: null,
    panelIndex: nextSelection.length > 0 ? nextSelection : null,
  });
}

/**
 * Deselects specified panels in the currently selected segment
 * 
 * This function removes the highlight from the specified panels and updates the selection state.
 * 
 * @param {Array|null} indices - Array of panel indices to deselect, or null to deselect all
 * @param {Function} setSelectedIndices - State setter function for selection indices
 * @param {Object} selectedIndices - Object containing current selection indices
 */
export function deselectPanels(indices, setSelectedIndices, selectedIndices) {  
  // Only proceed if indices is provided
  if (indices) {
    // Get current selection information
    const { segmentIndex, obstacleIndex, panelIndex } = selectedIndices;
    
    // Get panels in the segment
    const panels = POLYGONS[segmentIndex].panels ?? null;
    
    // Only proceed if panels exist
    if (panels) {
      // Loop through each index to deselect
      for (let index of indices) {
        applyPanelSelectionVisual(panels[index].panel, segmentIndex, false);
      }
      
      // Filter out deselected indices from the current selection
      const updated = panelIndex.filter(val => !indices.includes(val));
      
      // If no panels remain selected, set panelIndex to null
      if (updated.length === 0) {
        setSelectedIndices({ segmentIndex: segmentIndex, obstacleIndex: obstacleIndex, panelIndex: null });
      } else {
        // Otherwise, update with the remaining selected panels
        setSelectedIndices({ segmentIndex: segmentIndex, obstacleIndex: null, panelIndex: updated });
      }
    }
  }
}

/**
 * Deletes specified panels from a segment
 * 
 * This function removes the panel meshes from the scene and updates the POLYGONS data structure.
 * 
 * @param {number} segmentIndex - Index of the segment containing the panels
 * @param {Array} panelIndices - Array of panel indices to delete
 */
export function deletePanels(segmentIndex, panelIndices) {
  // Get the segment containing the panels
  let selectedSegment = POLYGONS[segmentIndex];
  
  // Get the scene to remove objects from
  let scene = sceneManager.scene;
  
  // Loop through each panel index to delete
  for (let index of panelIndices) {
    // Get the panel to delete
    const selectedPanel = selectedSegment.panels[index].panel;
    
    // Remove the panel from the scene
    scene.remove(selectedPanel);
    
    // Dispose of geometry and material to free memory
    selectedPanel.geometry.dispose();
    selectedPanel.material.dispose();
  }
  
  // Update the panels array to exclude deleted panels
  selectedSegment.panels = selectedSegment.panels.filter((_, index) => !panelIndices.includes(index));
}

/**
 * Removes panels from the scene that are not referenced in the POLYGONS data structure
 * 
 * This cleanup function ensures no orphaned panel meshes remain in the scene.
 * 
 * @param {Array} polygons - The POLYGONS array to check against
 */
export function removeExtraPanels(polygons) {
  // Array to store all panel meshes found in the scene
  const allPanels = [];
  
  // Flag to track if a panel was found in the POLYGONS data
  let found = false;
  
  // Array to store panels that need to be removed
  let selected = [];
  
  // Get all objects named "panel" from the scene
  sceneManager.scene.getObjectsByProperty("name", "panel", allPanels);
  
  // Check each panel to see if it's referenced in the POLYGONS data
  for (let panel of allPanels) {
    found = false;
    const idToMatch = panel.id;
    selected.push(panel);
    
    // Check each polygon for the panel
    for (let polygon of polygons) {
      const panelList = polygon.panels;
      for (let index of panelList) {
        // If panel is found in the data, don't remove it
        if (index.panel_id === idToMatch) {
          found = true;
          selected.pop();
          break;
        }
      }
      if (found === true) break;
    }
  }

  // Remove all panels that weren't found in the POLYGONS data
  for (let i = 0; i < selected.length; i++) {
    sceneManager.scene.remove(selected[i]);
    selected[i].geometry.dispose();
    selected[i].material.dispose();
  }
}

/**
 * Removes all grid helper meshes from the scene
 * 
 * This function cleans up grid visuals that are used for panel alignment.
 */
export function removeGrid() {
  // Get all objects named "grid" from the scene
  const grids = sceneManager.scene.getObjectsByProperty("name", "grid");
  
  // Remove each grid if found
  if (grids) {
    for (let grid of grids) {
      grid.removeFromParent();
      grid.geometry.dispose();
      grid.material.dispose();
    }
  }
}

/**
 * Applies tilt/rotation updates to one or more selected panels.
 *
 * @param {number} segmentIndex
 * @param {Array<number>} panelIndices
 * @param {Object} panelUpdates
 * @param {Array} polygonsState
 * @param {Function} setPolygons
 * @param {Function} setPanelsOK
 * @param {boolean} arePanelsOK
 * @returns {Object|null} Updated UI data for the first changed panel
 */
export function updateSelectedPanelsTransform(
  segmentIndex,
  panelIndices,
  panelUpdates = {},
  polygonsState = [],
  setPolygons = null,
  setPanelsOK = null,
  arePanelsOK = true
) {
  if (segmentIndex == null || !Array.isArray(panelIndices) || panelIndices.length === 0) {
    return null;
  }

  const segment = POLYGONS[segmentIndex];
  if (!segment || !Array.isArray(segment.panels)) return null;

  const uniqueIndices = [...new Set(panelIndices)]
    .filter((idx) => Number.isInteger(idx) && idx >= 0 && idx < segment.panels.length)
    .sort((a, b) => a - b);

  if (!uniqueIndices.length) return null;

  const snapshots = [];

  uniqueIndices.forEach((index) => {
    const panelRecord = segment.panels[index];
    const panelMesh = panelRecord?.panel;
    if (!panelMesh) return;

    const beforeUi = {
      ...(polygonsState?.[segmentIndex]?.panels?.[index] || {}),
    };
    const beforeQuaternion = panelMesh.quaternion.clone();
    const beforePosition = panelMesh.position.clone();
    const beforeTransform = panelMesh.userData?.panelTransform
      ? { ...panelMesh.userData.panelTransform }
      : null;
    const sourceData = { ...beforeUi, ...panelUpdates };
    const transform = applyTransformToPanel(panelMesh, segmentIndex, sourceData);
    if (!transform) return;

    const afterUi = {
      ...beforeUi,
      ...panelUpdates,
      tilt: transform.tilt,
      rotation: transform.rotation,
      azimuth: transform.azimuth,
    };

    snapshots.push({
      index,
      panel: panelMesh,
      beforeQuaternion,
      afterQuaternion: panelMesh.quaternion.clone(),
      beforePosition,
      afterPosition: panelMesh.position.clone(),
      beforeUi,
      afterUi,
      beforeTransform,
      afterTransform: {
        ...(panelMesh.userData?.panelTransform || {}),
      },
    });
  });

  if (!snapshots.length) {
    return null;
  }

  const changed = snapshots.some(
    (snap) =>
      hasQuaternionChanged(snap.beforeQuaternion, snap.afterQuaternion) ||
      hasPanelUiChanged(snap.beforeUi, snap.afterUi)
  );

  if (!changed) {
    return snapshots[0]?.afterUi || null;
  }

  patchPanelsUi(setPolygons, segmentIndex, snapshots, "after");
  if (typeof setPanelsOK === "function") {
    checkPanelsOK(segmentIndex, setPanelsOK, arePanelsOK);
  }

  pushUndoAction({
    label: snapshots.length > 1 ? "Update panels tilt/rotation" : "Update panel tilt/rotation",
    undo: () => {
      snapshots.forEach((snap) => {
        if (!snap.panel) return;
        snap.panel.quaternion.copy(snap.beforeQuaternion);
        snap.panel.position.copy(snap.beforePosition);
        if (snap.beforeTransform) {
          snap.panel.userData.panelTransform = { ...snap.beforeTransform };
        } else if (snap.panel.userData?.panelTransform) {
          delete snap.panel.userData.panelTransform;
        }
      });
      patchPanelsUi(setPolygons, segmentIndex, snapshots, "before");
      if (typeof setPanelsOK === "function") {
        checkPanelsOK(segmentIndex, setPanelsOK, arePanelsOK);
      }
    },
    redo: () => {
      snapshots.forEach((snap) => {
        if (!snap.panel) return;
        snap.panel.quaternion.copy(snap.afterQuaternion);
        snap.panel.position.copy(snap.afterPosition);
        snap.panel.userData.panelTransform = { ...snap.afterTransform };
      });
      patchPanelsUi(setPolygons, segmentIndex, snapshots, "after");
      if (typeof setPanelsOK === "function") {
        checkPanelsOK(segmentIndex, setPanelsOK, arePanelsOK);
      }
    },
  });

  return snapshots[0]?.afterUi || null;
}

/**
 * Applies module/orientation updates to selected panels.
 *
 * @param {number} segmentIndex
 * @param {Array<number>} panelIndices
 * @param {Object} panelUpdates
 * @param {Array} polygonsState
 * @param {Function} setPolygons
 * @param {Array} panelList
 * @param {Function} setPanelsOK
 * @param {boolean} arePanelsOK
 * @returns {Object|null}
 */
export function updateSelectedPanelsConfig(
  segmentIndex,
  panelIndices,
  panelUpdates = {},
  polygonsState = [],
  setPolygons = null,
  panelList = [],
  setPanelsOK = null,
  arePanelsOK = true
) {
  if (segmentIndex == null || !Array.isArray(panelIndices) || panelIndices.length === 0) {
    return null;
  }

  const segment = POLYGONS[segmentIndex];
  if (!segment || !Array.isArray(segment.panels)) return null;

  const uniqueIndices = [...new Set(panelIndices)]
    .filter((idx) => Number.isInteger(idx) && idx >= 0 && idx < segment.panels.length)
    .sort((a, b) => a - b);
  if (!uniqueIndices.length) return null;
  const selectedSet = new Set(uniqueIndices);

  const snapshots = [];

  uniqueIndices.forEach((index) => {
    const panelRecord = segment.panels[index];
    const panelMesh = panelRecord?.panel;
    if (!panelRecord || !panelMesh) return;

    const beforeUi = {
      ...(polygonsState?.[segmentIndex]?.panels?.[index] || {}),
    };
    const beforeRecordId = panelRecord.id;
    const beforeRecordArea = panelRecord.area;
    const beforePanelType = panelMesh.panelType;
    const beforePosition = panelMesh.position.clone();
    const beforeWidth = Number(panelMesh?.geometry?.parameters?.width || 0);
    const beforeHeight = Number(panelMesh?.geometry?.parameters?.height || 0);

    const desiredModuleId = panelUpdates.id ?? beforeUi.id ?? panelRecord.id;
    const desiredOrientation =
      panelUpdates.orientation ?? beforeUi.orientation ?? "Portrait";
    const panelCatalog = getPanelCatalogById(panelList, desiredModuleId);
    if (!panelCatalog) return;

    const { widthM, heightM, areaSqft } = getOrientedDimensions(
      panelCatalog,
      desiredOrientation
    );
    if (!(widthM > 0 && heightM > 0)) return;

    const geomChanged =
      Math.abs(beforeWidth - widthM) > 1e-8 || Math.abs(beforeHeight - heightM) > 1e-8;

    if (geomChanged) {
      const oldGeometry = panelMesh.geometry;
      panelMesh.geometry = new THREE.PlaneGeometry(widthM, heightM);
      oldGeometry?.dispose?.();
      refreshPanelWireframe(panelMesh);
    }

    panelRecord.id = panelCatalog.id;
    panelRecord.area = areaSqft;
    panelMesh.panelType = desiredOrientation === "Landscape" ? "landscape" : "potrait";
    applyPanelTexture(panelMesh, panelCatalog.id, desiredOrientation);

    const panelTransform = panelMesh?.userData?.panelTransform || {};
    const afterUi = {
      ...beforeUi,
      ...panelUpdates,
      id: panelCatalog.id,
      orientation: desiredOrientation,
      tilt: beforeUi?.tilt ?? panelTransform.tilt ?? 0,
      rotation: beforeUi?.rotation ?? panelTransform.rotation ?? 0,
      azimuth: beforeUi?.azimuth ?? panelTransform.azimuth ?? 0,
    };

    snapshots.push({
      index,
      panel: panelMesh,
      beforeUi,
      afterUi,
      beforePosition,
      afterPosition: panelMesh.position.clone(),
      beforeRecordId,
      afterRecordId: panelCatalog.id,
      beforeRecordArea,
      afterRecordArea: areaSqft,
      beforePanelType,
      afterPanelType: panelMesh.panelType,
      beforeWidth,
      beforeHeight,
      afterWidth: widthM,
      afterHeight: heightM,
    });
  });

  if (!snapshots.length) return null;

  reflowUpdatedPanelLayout(segmentIndex, snapshots, panelUpdates);

  const EPS = 1e-8;
  const changed = snapshots.some((snap) => {
    const idChanged = String(snap.beforeRecordId) !== String(snap.afterRecordId);
    const orientationChanged =
      String(snap.beforeUi?.orientation ?? "") !== String(snap.afterUi?.orientation ?? "");
    const geomChanged =
      Math.abs(Number(snap.beforeWidth || 0) - Number(snap.afterWidth || 0)) > EPS ||
      Math.abs(Number(snap.beforeHeight || 0) - Number(snap.afterHeight || 0)) > EPS;
    const moved =
      snap.beforePosition?.distanceTo?.(snap.afterPosition) > EPS;
    return idChanged || orientationChanged || geomChanged || moved;
  });

  patchPanelsUi(setPolygons, segmentIndex, snapshots, "after");
  snapshots.forEach((snap) =>
    applyPanelSelectionVisual(snap.panel, segmentIndex, selectedSet.has(snap.index))
  );
  if (typeof setPanelsOK === "function") {
    checkPanelsOK(segmentIndex, setPanelsOK, arePanelsOK);
  }

  if (!changed) {
    return snapshots[0]?.afterUi || null;
  }

  const applyGeometrySize = (panelMesh, width, height) => {
    if (!panelMesh) return;
    const currentWidth = Number(panelMesh?.geometry?.parameters?.width || 0);
    const currentHeight = Number(panelMesh?.geometry?.parameters?.height || 0);
    if (
      Math.abs(currentWidth - Number(width || 0)) <= EPS &&
      Math.abs(currentHeight - Number(height || 0)) <= EPS
    ) {
      return;
    }

    const oldGeometry = panelMesh.geometry;
    panelMesh.geometry = new THREE.PlaneGeometry(Number(width || 0), Number(height || 0));
    oldGeometry?.dispose?.();
    refreshPanelWireframe(panelMesh);
  };

  pushUndoAction({
    label: snapshots.length > 1 ? "Update panels module" : "Update panel module",
    undo: () => {
      snapshots.forEach((snap) => {
        const panelRecord = POLYGONS?.[segmentIndex]?.panels?.[snap.index];
        const panelMesh = panelRecord?.panel || snap.panel;
        if (!panelRecord || !panelMesh) return;

        applyGeometrySize(panelMesh, snap.beforeWidth, snap.beforeHeight);
        panelRecord.id = snap.beforeRecordId;
        panelRecord.area = snap.beforeRecordArea;
        panelMesh.panelType = snap.beforePanelType;
        panelMesh.position.copy(snap.beforePosition);
        applyPanelTexture(panelMesh, snap.beforeRecordId, snap.beforeUi?.orientation ?? "Portrait");
      });

      patchPanelsUi(setPolygons, segmentIndex, snapshots, "before");
      snapshots.forEach((snap) =>
        applyPanelSelectionVisual(snap.panel, segmentIndex, selectedSet.has(snap.index))
      );
      if (typeof setPanelsOK === "function") {
        checkPanelsOK(segmentIndex, setPanelsOK, arePanelsOK);
      }
    },
    redo: () => {
      snapshots.forEach((snap) => {
        const panelRecord = POLYGONS?.[segmentIndex]?.panels?.[snap.index];
        const panelMesh = panelRecord?.panel || snap.panel;
        if (!panelRecord || !panelMesh) return;

        applyGeometrySize(panelMesh, snap.afterWidth, snap.afterHeight);
        panelRecord.id = snap.afterRecordId;
        panelRecord.area = snap.afterRecordArea;
        panelMesh.panelType = snap.afterPanelType;
        panelMesh.position.copy(snap.afterPosition);
        applyPanelTexture(panelMesh, snap.afterRecordId, snap.afterUi?.orientation ?? "Portrait");
      });

      patchPanelsUi(setPolygons, segmentIndex, snapshots, "after");
      snapshots.forEach((snap) =>
        applyPanelSelectionVisual(snap.panel, segmentIndex, selectedSet.has(snap.index))
      );
      if (typeof setPanelsOK === "function") {
        checkPanelsOK(segmentIndex, setPanelsOK, arePanelsOK);
      }
    },
  });

  return snapshots[0]?.afterUi || null;
}

/**
 * Changes the orientation of a panel (portrait to landscape or vice versa)
 * 
 * This function creates a new panel with the desired orientation at the same location,
 * then deletes the original panel.
 * 
 * @param {THREE.Scene} scene - The scene where the panel is rendered
 * @param {THREE.Camera} camera - The camera for raycasting and positioning
 * @param {number} segmentIndex - Index of the segment containing the panel
 * @param {Object} solarPanel - Object containing panel specifications with updated orientation
 * @param {Function} setPanels - Function to update panel list in state
 * @param {Array} panelList - Array of available panel types/models
 * @param {THREE.Mesh} panel - The current panel mesh to replace
 * @param {number} panelIndex - Index of the panel to replace
 * @param {Function} deletePanel - Function to update panel deletion state
 * @param {Function} setPanelsOK - Function to update panel validity state
 * @param {boolean} arePanelsOK - Current panel validity state
 * @returns {Promise} A promise that resolves when the operation is complete
 */
export async function changeOrientation(scene, camera, segmentIndex, solarPanel, setPanels, panelList, panel, panelIndex, deletePanel, setPanelsOK, arePanelsOK) {
  // Create a new panel manager with the updated orientation
  const newPanel = new PanelManager(
    scene,
    camera,
    segmentIndex,
    solarPanel,
    setPanels,
    panelList,
    null,
    null, 
    true
  );
  
  // Position the new panel at the same location as the old one
  await newPanel.handleMouseMove(panel.position.project(camera));
  await newPanel.handleMouseMove(newPanel.panel.position.project(camera));
  
  // Add the new panel to the segment
  newPanel.addPanel();

  // Remove the old panel
  deletePanels(segmentIndex, [panelIndex]);
  deletePanel([panelIndex]);

  // Get the newly created panel for validation
  let totalPanels = POLYGONS[segmentIndex].panels.length;
  let current = POLYGONS[segmentIndex].panels[totalPanels - 1].panel;
  
  // Update visual feedback based on placement validity
  if (!panelInSegment(current, segmentIndex)) {
    setPanelWireframeStyle(current, { opacity: 1, color: COLORS.red });
  } else {
    setPanelWireframeStyle(current, { opacity: 1, color: COLORS.yellow });
  }
  
  // Check if all panels in the segment are valid
  checkPanelsOK(segmentIndex, setPanelsOK, arePanelsOK);
}

/**
 * Checks if all panels in a segment are validly placed
 * 
 * This function validates each panel's placement and updates the panelsOK state.
 * 
 * @param {number} segmentIndex - Index of the segment to check
 * @param {Function} setPanelsOK - Function to update panel validity state
 * @param {boolean} arePanelsOK - Current panel validity state
 */
export function checkPanelsOK(segmentIndex, setPanelsOK, arePanelsOK) {
  // Check each panel in the segment
  for(let panel of POLYGONS[segmentIndex].panels) {
    // If panel is valid, set panelsOK to true
    if (panelInSegment(panel.panel, segmentIndex)) {
      setPanelsOK(true);      
    } else {
      // If any panel is invalid, set panelsOK to false and stop checking
      setPanelsOK(false);      
      break;
    }
    
    // Stop checking if panelsOK is already false
    if(!arePanelsOK) break;
  }
}
